import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, MessageSquare, User, Calendar, Bell, Shield, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

interface BottomNavItem {
  name: string;
  icon: React.ElementType;
  path: string;
}

interface BottomNavProps {
  userType: 'patient' | 'doctor' | 'admin' | 'nurse' | 'clinic';
  unreadCount?: number;
}

export const BottomNav: React.FC<BottomNavProps> = ({ userType, unreadCount = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ SÉCURITÉ: Items de navigation adaptés au type d'espace (évite /clinic/messages inexistant)
  const getNavItems = (): BottomNavItem[] => {
    switch (userType) {
      case 'clinic': {
        const storedRole = (localStorage.getItem('activeClinicRole') || '').toUpperCase();
        const storedUser = localStorage.getItem('user');
        let isNurseUser = storedRole === 'CLINIC_NURSE';
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            if (parsed.role?.toLowerCase() === 'nurse') isNurseUser = true;
          } catch {
            // ignore
          }
        }

        if (isNurseUser) {
          return [
            { name: 'File & Triage', icon: Calendar, path: '/clinic/triage' },
            { name: 'Orientations', icon: Heart, path: '/clinic/referrals' },
          ];
        }

        return [
          { name: 'Command Center', icon: Home, path: '/clinic' },
          { name: 'File & Triage', icon: Calendar, path: '/clinic/triage' },
          { name: 'Praticiens', icon: User, path: '/clinic/practitioners' },
          { name: 'Paramètres', icon: Bell, path: '/clinic/settings' },
        ];
      }
      case 'admin':
        return [
          { name: 'Dashboard', icon: Home, path: '/admin' },
          { name: 'Portail Clinique', icon: Shield, path: '/clinic' },
          { name: 'Sécurité', icon: Shield, path: '/admin/security' },
          { name: 'Paramètres', icon: Bell, path: '/admin/settings' },
        ];
      case 'nurse':
        return [
          { name: 'Tableau de bord', icon: Home, path: '/nurse' },
          { name: 'Triage', icon: Heart, path: '/nurse/triage' },
          { name: 'Profil', icon: User, path: '/nurse/profile' },
          { name: 'Paramètres', icon: Bell, path: '/nurse/settings' },
        ];
      default:
        // patient, doctor
        return [
          { name: 'Tableau de bord', icon: Home, path: `/${userType}` },
          { name: 'Messages', icon: MessageSquare, path: `/${userType}/messages` },
          { name: 'Rendez-vous', icon: Calendar, path: `/${userType}/appointments` },
          { name: 'Profil', icon: User, path: `/${userType}/profile` },
        ];
    }
  };

  const navItems = getNavItems();

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl z-50 lg:hidden safe-area-bottom pb-1 shadow-[0_-10px_30px_rgba(0,50,115,0.05)]">
      <div className="flex items-center justify-around h-16 px-4">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || (item.path !== `/${userType}` && location.pathname.startsWith(item.path));
          const Icon = item.icon;

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`relative flex flex-col items-center justify-center w-full h-full transition-all duration-300 ${
                isActive ? 'text-med-primary' : 'text-slate-400'
              }`}
            >
              {isActive && (
                <motion.div 
                  layoutId="bottom-nav-active"
                  className="absolute inset-x-2 top-2 bottom-2 bg-med-primary/5 rounded-xl -z-10"
                />
              )}
              
              <div className="relative">
                <Icon size={24} className={`transition-transform duration-500 ${isActive ? 'scale-110' : ''}`} strokeWidth={isActive ? 2.5 : 2} />
                {(item.name === 'Messages' || item.name === 'Dashboard') && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-med-success text-[10px] font-bold text-med-primary shadow-lg shadow-med-success/20">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {isActive && (
                   <motion.div 
                     initial={{ scale: 0 }}
                     animate={{ scale: 1 }}
                     className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-med-success rounded-full shadow-[0_0_8px_theme(colors.med.success)]"
                   />
                )}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest mt-1.5 transition-all ${isActive ? 'opacity-100' : 'opacity-40'}`}>
                {item.name}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
